import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Table, Modal, Button, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import { getDepositAction, getDepositReceiptAction } from '../store/depositActions';
import { type DepositResponse, type DepositReceiptData } from '../store/depositTypes';
import dayjs from 'dayjs';
import DepositPaymentModal from '../DepositPayment';
import DepositCloseModal from '../DepositClose';
import DepositTransactionsModal from '../DepositTransactions';
import DepositReceiptPrintable from '../DepositReceiptPrintable';
// import { resetDeposits } from '../store/depositReducer';
import fmt from 'indian-number-format';

export default function DepositTableIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { deposits }: any = useSelector<any>((state) => state?.deposit);

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const [branch, setBranch] = useState<any>('');

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> = [
        { label: 'All', value: 'all' },
        ...parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        })),
      ];
      setBranchOptions(branchOptionTemp);
      setBranch('all');
    }
  }, []);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // const [phonenumber, setPhonenumber] = useState<string>('');

  const [payModal, setPayModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [transactionsModal, setTransactionsModal] = useState(false);
  const [printModal, setPrintModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<DepositResponse>();
  const [receiptData, setReceiptData] = useState<DepositReceiptData | null>(null);

  const handlePayClose = () => {
    setPayModal(false);
    void getDeposits();
  };

  const handleCloseModalClose = () => {
    setCloseModal(false);
    void getDeposits();
  };

  const getDeposits = async (): Promise<void> => {
    setIsLoading(true);
    await getDepositAction(dispatch, {
      branch,
      // phoneNumber: phonenumber,
    });
    setIsLoading(false);
  };

  // useEffect(() => {
  //   if (phonenumber !== '' && phonenumber.length > 8) {
  //     void getDeposits();
  //   }
  // }, [phonenumber]);

  const handlePay = (record: DepositResponse) => {
    setSelectedDeposit(record);
    setPayModal(true);
  };

  const handleClose = (record: DepositResponse) => {
    setSelectedDeposit(record);
    setCloseModal(true);
  };

  const handleTransactions = (record: DepositResponse) => {
    setSelectedDeposit(record);
    setTransactionsModal(true);
  };

  const handleTransactionsModalClose = () => {
    setTransactionsModal(false);
  };

  const handlePrint = async (record: DepositResponse) => {
    if (record.depositNumber === undefined) return;
    setIsLoading(true);
    const response = await getDepositReceiptAction(dispatch, record.depositNumber);
    setIsLoading(false);
    if (response?.receipt !== undefined) {
      setReceiptData(response.receipt);
      setPrintModal(true);
    }
  };

  const handlePrintModalClose = () => {
    setPrintModal(false);
    setReceiptData(null);
  };

  // Filter deposits into current and closed
  const currentDeposits = useMemo(() => {
    return deposits.filter(
      (deposit: DepositResponse) => deposit.status !== 'closed'
    );
  }, [deposits]);

  const closedDeposits = useMemo(() => {
    return deposits.filter(
      (deposit: DepositResponse) => deposit.status === 'closed'
    );
  }, [deposits]);

  const totalDepositAmount = useMemo(() => {
    return currentDeposits.reduce(
      (sum: number, deposit: DepositResponse) => sum + (deposit.amount ?? 0),
      0
    );
  }, [currentDeposits]);

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: DepositResponse, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Deposit Number',
      dataIndex: 'depositNumber',
      key: 'depositNumber',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{record.depositNumber}</p>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{fmt.format(record.amount)}</p>
      ),
    },
    {
      title: 'Nominee',
      dataIndex: 'nominee',
      key: 'nominee',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{record.nominee}</p>
      ),
    },
    {
      title: 'Pending Interest',
      dataIndex: 'pendingInterest',
      key: 'pendingInterest',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>
          {record.pendingInterest != null
            ? `${fmt.format(record.pendingInterest)} (${
                record.interestDays ?? 0
              } days) @ ${record.interest}%`
            : record.interest != null
            ? `0 (0 days) @ ${record.interest}%`
            : '-'}
        </p>
      ),
    },
    {
      title: 'Invested Date',
      dataIndex: 'investedDate',
      key: 'investedDate',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{dayjs(String(record.investedDate)).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 320,
      render: (_: any, record: DepositResponse, index: number) => {
        const canClose = (record.pendingInterest ?? 0) === 0;
        const canPay = (record.pendingInterest ?? 0) > 0;
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            {canPay && (
              <p
                style={{
                  width: 73,
                  height: 34,
                  borderRadius: 12,
                  backgroundColor: '#DD1E2D',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  handlePay(record);
                }}
              >
                Pay
              </p>
            )}
            {canClose && (
              <p
                style={{
                  width: 73,
                  height: 34,
                  borderRadius: 12,
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  handleClose(record);
                }}
              >
                Close
              </p>
            )}
            <p
              style={{
                width: 73,
                height: 34,
                borderRadius: 12,
                backgroundColor: '#DD1E2D',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                handleTransactions(record);
              }}
            >
              View
            </p>
            <p
              style={{
                width: 73,
                height: 34,
                borderRadius: 12,
                backgroundColor: '#DD1E2D',
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                void handlePrint(record);
              }}
            >
              Print
            </p>
          </div>
        );
      },
    },
  ];

  const closedColumns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: DepositResponse, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{fmt.format(record.amount)}</p>
      ),
    },
    {
      title: 'Nominee',
      dataIndex: 'nominee',
      key: 'nominee',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{record.nominee}</p>
      ),
    },
    {
      title: 'Invested Date',
      dataIndex: 'investedDate',
      key: 'investedDate',
      render: (_: any, record: DepositResponse, index: number) => (
        <p>{dayjs(String(record.investedDate)).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 100,
      render: (_: any, record: DepositResponse, index: number) => (
        <p
          style={{
            width: 73,
            height: 34,
            borderRadius: 12,
            backgroundColor: '#DD1E2D',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            handleTransactions(record);
          }}
        >
          View
        </p>
      ),
    },
  ];

  return (
    <div className="deposit__table__page">
      <Spin spinning={isLoading} fullscreen />
      {/* <h4>Enter phone number</h4> */}
      {/* <Input
        allowClear
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setPhonenumber(e.target.value);
          if (e.target.value === '') {
            dispatch(resetDeposits());
          }
        }}
        className="deposit__input"
        placeholder="Phonenumber"
      /> */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 14,
          alignItems: 'center',
        }}
      >
        <Select
          value={branch}
          onChange={(value: string): void => {
            setBranch(value);
          }}
          style={{ width: 311, height: 56 }}
          placeholder="Select branch"
          options={branchOptions}
          className="expense__input"
        />
        <Button onClick={getDeposits} className="search__button">
          <span>Search</span>
        </Button>
      </div>
      {Boolean(currentDeposits.length) && (
        <div style={{ width: '100%', marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>Current Deposits</h3>
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '6px 16px', fontWeight: 600, fontSize: 15 }}>
              Total Amount: <span style={{ color: '#DD1E2D' }}>{fmt.format(totalDepositAmount)}</span>
            </div>
          </div>
          <Table
            dataSource={currentDeposits}
            columns={columns}
            pagination={false}
            rowKey={(record: DepositResponse) => record.id}
          />
        </div>
      )}
      {Boolean(closedDeposits.length) && (
        <div style={{ width: '100%', marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Closed Deposits</h3>
          <Table
            dataSource={closedDeposits}
            columns={closedColumns}
            pagination={false}
            rowKey={(record: DepositResponse) => record.id}
          />
        </div>
      )}
      {/* {phonenumber !== '' && deposits.length === 0 && (
        <div style={{ width: '100%' }}>
          <p>No deposits found for the entered phone number</p>
        </div>
      )} */}
      <Modal
        width={820}
        title={`Pay Deposit${
          selectedDeposit?.customerName != null &&
          selectedDeposit.customerName !== ''
            ? ` - ${selectedDeposit.customerName}`
            : ''
        }`}
        open={payModal}
        onCancel={handlePayClose}
        destroyOnClose
        footer={() => <></>}
      >
        {selectedDeposit !== undefined && (
          <DepositPaymentModal
            closeModalNOOP={handlePayClose}
            depositDetail={selectedDeposit}
          />
        )}
      </Modal>
      <Modal
        width={820}
        title={`Close Deposit${
          selectedDeposit?.customerName != null &&
          selectedDeposit.customerName !== ''
            ? ` - ${selectedDeposit.customerName}`
            : ''
        }`}
        open={closeModal}
        onCancel={handleCloseModalClose}
        destroyOnClose
        footer={() => <></>}
      >
        {selectedDeposit !== undefined && (
          <DepositCloseModal
            closeModalNOOP={handleCloseModalClose}
            depositDetail={selectedDeposit}
          />
        )}
      </Modal>
      <Modal
        width={900}
        title={`Deposit Transactions${
          selectedDeposit?.customerName != null &&
          selectedDeposit.customerName !== ''
            ? ` - ${selectedDeposit.customerName}`
            : ''
        }`}
        open={transactionsModal}
        onCancel={handleTransactionsModalClose}
        destroyOnClose
        footer={() => <></>}
      >
        {selectedDeposit !== undefined && (
          <DepositTransactionsModal
            transactions={selectedDeposit.transactions}
            depositNumber={selectedDeposit.depositNumber}
          />
        )}
      </Modal>
      <Modal
        width={800}
        title="Deposit Receipt"
        open={printModal}
        onCancel={handlePrintModalClose}
        destroyOnClose
        footer={() => (
          <Button onClick={handlePrintModalClose}>Close</Button>
        )}
      >
        {receiptData !== null && (
          <DepositReceiptPrintable receipt={receiptData} />
        )}
      </Modal>
    </div>
  );
}
